/**
 * Aperçu d'un conflit de fusion sur le plan affiché : les versions de l'entité
 * en conflit, lues dans les bases annexes (cible, fusionnée), sont montrées en
 * FANTÔMES — clones ajoutés à la vue seulement (`view.add`), comme les aperçus
 * des draggers (cf. `entities/ghost.ts`). Rien n'entre dans la base affichée :
 * ni historique, ni sauvegarde.
 *
 * Le clone reste rattaché à SA base annexe : ODA refuse de le rattacher au plan
 * affiché (`setDatabaseDefaults` d'une autre base lève une exception), et ses
 * références (calque, style, définition de bloc) y sont lues au rendu. D'où la
 * règle : `clear()` AVANT de fermer les bases annexes. Seule la couleur est
 * forcée (bleu cible / orange fusionnée).
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { OpenMode } from "../core/constants";
import { findEntityByHandle, openAs } from "../core/oda";
import { Logger } from "@/logger";

export interface PreviewVersion {
  db: DrawingModule.OdDbDatabase;
  handle: string;
  rgb: { r: number; g: number; b: number };
}

export class MergePreview {
  private ghosts: DrawingModule.OdDbEntity[] = [];

  constructor(private viewer: DRDrawingViewer) {}

  /** Remplace l'aperçu courant par les versions données. */
  show(versions: PreviewVersion[]): void {
    this.clear();
    const view = this.viewer.app?.getDevice()?.viewAt(0);
    if (!view) return;
    for (const v of versions) {
      try {
        const ghost = this.createGhost(v);
        if (!ghost) continue;
        this.ghosts.push(ghost);
        view.add(ghost, null as unknown as DrawingModule.OdGsModel);
      } catch (e) {
        Logger.captureException(e, {
          feature: "branch_merge",
          site: "preview.show",
          handle: v.handle,
        });
      }
    }
    this.viewer.app?.getDevice()?.invalidate();
    this.viewer.update();
  }

  /** Retire l'aperçu (sans effet s'il n'y en a pas). */
  clear(): void {
    if (this.ghosts.length === 0) return;
    const device = this.viewer.app?.getDevice();
    const view = device?.viewAt(0);
    const ghosts = this.ghosts;
    this.ghosts = [];
    if (view) for (const ghost of ghosts) view.erase(ghost);
    device?.invalidate();
    try {
      this.viewer.update();
    } catch (e) {
      Logger.captureException(e, {
        feature: "branch_merge",
        site: "preview.clear",
      });
    } finally {
      // Seulement après le rendu : view.erase() ne fait que mettre en file, le
      // moteur tient encore le pointeur (cf. BasePointDragger.destroyGhosts).
      for (const ghost of ghosts) deleteObj(ghost);
    }
  }

  private createGhost(v: PreviewVersion): DrawingModule.OdDbEntity | null {
    const module = this.viewer.module;
    const id = findEntityByHandle(module, v.db, v.handle);
    if (!id) return null;
    let entity: DrawingModule.OdDbEntity | null = null;
    let clonePtr: DrawingModule.OdRxObjectPtr | null = null;
    try {
      entity = openAs(id, module.OdDbEntity, OpenMode.kForRead);
      clonePtr = entity.clone();
      const clone = module.OdDbEntity.cast(clonePtr.get());
      if (!clone) return null;
      const color = new module.OdCmColor();
      color.setRGB(v.rgb.r, v.rgb.g, v.rgb.b);
      clone.setColor(color, true);
      deleteObj(color);
      return clone;
    } finally {
      if (clonePtr) deleteObj(clonePtr);
      if (entity) deleteObj(entity);
      deleteObj(id);
    }
  }
}
